import React, { useState, useEffect } from 'react';
import { Copy, Check } from 'lucide-react';
import { supabase } from '../utils/supabase';
import { useCallback } from 'react';

interface SchedulingLinkProps {
  doubleId: string;
  initialName?: string;
}

const SchedulingLink = ({ doubleId, initialName }: SchedulingLinkProps) => {
  const [name, setName] = useState(initialName || '');
  const [isEditing, setIsEditing] = useState(!initialName);
  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const baseUrl = 'mycal.live/';
  const schedulingUrl = `https://${baseUrl}${name.trim()}`;

  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => setIsCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isCopied]);

  const handleDoubleClick = useCallback(() => {
    if (!isEditing) {
      setIsEditing(true);
    }
  }, [isEditing]);

  const handleSave = async () => {
    try {
      const { error: updateError } = await supabase
        .from('doubles')
        .update({ scheduling_link: name })
        .eq('id', doubleId);

      if (updateError) throw updateError;
      setIsEditing(false);
      setError(null);
    } catch (err) {
      console.error('Error updating scheduling link:', err);
      setError('Failed to save scheduling link');
    }
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling up
    navigator.clipboard.writeText(schedulingUrl);
    setIsCopied(true);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Scheduling Link
      </label>
      <div 
        className="flex items-center gap-2 relative"
        onDoubleClick={handleDoubleClick}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-1 flex items-center gap-1 px-3 py-2 bg-gray-50 rounded-lg">
          <span className="text-gray-500">mycal.live/</span>
          {isEditing ? (
            <input
              type="text"
              value={name}
              onChange={(e) => {
                const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                setName(value);
              }}
              className="flex-1 bg-transparent border-none focus:outline-none"
              placeholder="your-name"
              autoFocus
            />
          ) : (
            <span className="cursor-text">{name}</span>
          )}
        </div>
        {isEditing ? (
          <button
            onClick={handleSave}
            disabled={!name.trim()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all disabled:bg-gray-100 disabled:text-gray-400"
          >
            Save
          </button>
        ) : (
          <button
            onClick={handleCopy}
            onMouseDown={(e) => e.stopPropagation()}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
          >
            {isCopied ? (
              <Check className="h-5 w-5 text-green-500" />
            ) : (
              <Copy className="h-5 w-5" />
            )}
          </button>
        )}
      </div>
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default SchedulingLink;